import { Controller } from "@hotwired/stimulus"

/**
 * Controller for managing contact detail modals in the PWA contacts page.
 * Handles opening modals and populating them with contact or office information.
 * 
 * @extends Controller
 */
export default class extends Controller {
  static targets = ["modal", "name", "office", "phone", "emailText", "callLink", "emailLink", "image", "imageContainer"]

  /**
   * Opens the contact modal and populates it with contact information.
   * Triggered when a contact item is clicked.
   * 
   * @param {Event} event - The click event containing contact data in dataset
   * @param {string} event.currentTarget.dataset.contactName - The contact's name
   * @param {string} event.currentTarget.dataset.contactPhone - The contact's phone number
   * @param {string} event.currentTarget.dataset.contactEmail - The contact's email address
   * @param {string} event.currentTarget.dataset.officeTitle - The office title/location
   */
  openModal(event) {
    const contactElement = event.currentTarget
    const officeTitle = contactElement.dataset.officeTitle
    
    this.populateModal({
      name: contactElement.dataset.contactName,
      phone: contactElement.dataset.contactPhone,
      email: contactElement.dataset.contactEmail,
      image: contactElement.dataset.contactImage
    })
    
    this.officeTarget.textContent = officeTitle
    this.modalTarget.showModal()
  }
  
  /**
   * Opens the office modal and populates it with office information.
   * Triggered when an office item is clicked.
   * 
   * @param {Event} event - The click event containing office data in dataset
   * @param {string} event.currentTarget.dataset.officeName - The office name
   * @param {string} event.currentTarget.dataset.officePhone - The office phone number
   * @param {string} event.currentTarget.dataset.officeEmail - The office email address
   */
  openOfficeModal(event) {
    const officeElement = event.currentTarget
    
    this.populateModal({
      name: officeElement.dataset.officeName,
      phone: officeElement.dataset.officePhone,
      email: officeElement.dataset.officeEmail,
      image: officeElement.dataset.officeImage
    })
    
    this.modalTarget.showModal()
  }
  
  /**
   * Populates modal content with provided data.
   * Handles showing/hiding elements based on data availability.
   * 
   * @private
   * @param {Object} data - The data to populate
   * @param {string} data.name - The name to display
   * @param {string} data.phone - The phone number
   * @param {string} data.email - The email address
   * @param {string} data.image - The image source URL
   */
  populateModal({ name, phone, email, image }) {
    this.nameTarget.textContent = name
    
    // Show/hide image based on availability
    if (image && image.trim()) {
      this.imageTarget.src = image
      this.imageTarget.alt = name
      this.imageContainerTarget.style.display = 'block'
    } else {
      this.imageContainerTarget.style.display = 'none'
    }
    
    // Show/hide phone link based on availability
    if (phone && phone.trim()) {
      this.phoneTarget.textContent = phone
      this.callLinkTarget.href = `tel:${phone.replace(/\D/g, '')}`
      this.callLinkTarget.style.display = 'block'
    } else {
      this.callLinkTarget.style.display = 'none'
    }
    
    // Show/hide email link based on availability
    if (email && email.trim()) {
      this.emailTextTarget.textContent = email
      this.emailLinkTarget.href = `mailto:${email}`
      this.emailLinkTarget.style.display = 'block'
    } else {
      this.emailLinkTarget.style.display = 'none'
    }
  }
}